import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { Button } from '@/components/ui/button';
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Heading1, Heading2, Heading3, Code, AlignLeft, AlignCenter, AlignRight, AlignJustify, Quote, Minus } from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const RichTextEditor = ({ 
  value, 
  onChange, 
  placeholder = "Describe what proof you need..."
}: RichTextEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      TextAlign.configure({ 
        types: ['heading', 'paragraph'],
        defaultAlignment: 'left',
      }),
      Underline,
      Placeholder.configure({ 
        placeholder,
        showOnlyWhenEditable: true,
      }),
    ],
    content: value,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none',
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Sync editor content when value prop changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  if (!editor) return null;

  const toggleBold = () => editor.chain().focus().toggleBold().run();
  const toggleItalic = () => editor.chain().focus().toggleItalic().run();
  const toggleUnderline = () => editor.chain().focus().toggleUnderline().run();
  const toggleBulletList = () => editor.chain().focus().toggleBulletList().run();
  const toggleOrderedList = () => editor.chain().focus().toggleOrderedList().run();
  const setHeading1 = () => editor.chain().focus().setNode('heading', { level: 1 }).run();
  const setHeading2 = () => editor.chain().focus().setNode('heading', { level: 2 }).run();
  const setHeading3 = () => editor.chain().focus().setNode('heading', { level: 3 }).run();
  const setCodeBlock = () => editor.chain().focus().setCodeBlock().run();
  const toggleBlockquote = () => editor.chain().focus().toggleBlockquote().run();
  const setHorizontalRule = () => editor.chain().focus().setHorizontalRule().run();
  const setTextAlignLeft = () => editor.chain().focus().setTextAlign('left').run();
  const setTextAlignCenter = () => editor.chain().focus().setTextAlign('center').run();
  const setTextAlignRight = () => editor.chain().focus().setTextAlign('right').run();
  const setTextAlignJustify = () => editor.chain().focus().setTextAlign('justify').run();

  const isBold = editor.isActive('bold');
  const isItalic = editor.isActive('italic');
  const isUnderline = editor.isActive('underline');
  const isBulletList = editor.isActive('bulletList');
  const isOrderedList = editor.isActive('orderedList');
  const isHeading1 = editor.isActive('heading', { level: 1 });
  const isHeading2 = editor.isActive('heading', { level: 2 });
  const isHeading3 = editor.isActive('heading', { level: 3 });
  const isCodeBlock = editor.isActive('codeBlock');
  const isBlockquote = editor.isActive('blockquote');
  const isHorizontalRule = editor.isActive('horizontalRule');
  const textAlign = editor.getAttributes('textStyle').textAlign || 'left';

  return (
    <div className="border-2 border-[hsl(var(--paper-edge))] rounded-lg overflow-hidden dark:border-[hsl(var(--paper-edge))]">
      {/* Toolbar */}
      <div className="bg-[hsl(var(--paper))] border-b-2 border-[hsl(var(--paper-edge))] p-2 flex flex-wrap gap-1 dark:bg-[hsl(var(--paper))] dark:border-[hsl(var(--paper-edge))]">
        <Button 
          variant="ghost" 
          size="sm" 
          className={`h-8 w-8 p-0 ${isBold ? 'bg-[hsl(22_65%_28%)] text-white' : 'hover:bg-[hsl(22_65%_28%)]/10'} dark:hover:bg-[hsl(22_65%_28%)]/10`}
          onClick={toggleBold}
          title="Bold"
        >
          <Bold className="h-4 w-4 dark:text-white" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`h-8 w-8 p-0 ${isItalic ? 'bg-[hsl(22_65%_28%)] text-white' : 'hover:bg-[hsl(22_65%_28%)]/10'} dark:hover:bg-[hsl(22_65%_28%)]/10`}
          onClick={toggleItalic}
          title="Italic"
        >
          <Italic className="h-4 w-4 dark:text-white" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`h-8 w-8 p-0 ${isUnderline ? 'bg-[hsl(22_65%_28%)] text-white' : 'hover:bg-[hsl(22_65%_28%)]/10'} dark:hover:bg-[hsl(22_65%_28%)]/10`}
          onClick={toggleUnderline}
          title="Underline"
        >
          <UnderlineIcon className="h-4 w-4 dark:text-white" />
        </Button>
        <div className="w-px h-6 bg-[hsl(var(--paper-edge))] mx-1 dark:bg-[hsl(var(--paper-edge))]"></div>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`h-8 w-8 p-0 ${isBulletList ? 'bg-[hsl(22_65%_28%)] text-white' : 'hover:bg-[hsl(22_65%_28%)]/10'} dark:hover:bg-[hsl(22_65%_28%)]/10`}
          onClick={toggleBulletList}
          title="Bullet List"
        >
          <List className="h-4 w-4 dark:text-white" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`h-8 w-8 p-0 ${isOrderedList ? 'bg-[hsl(22_65%_28%)] text-white' : 'hover:bg-[hsl(22_65%_28%)]/10'} dark:hover:bg-[hsl(22_65%_28%)]/10`}
          onClick={toggleOrderedList}
          title="Numbered List"
        >
          <ListOrdered className="h-4 w-4 dark:text-white" />
        </Button>
        <div className="w-px h-6 bg-[hsl(var(--paper-edge))] mx-1 dark:bg-[hsl(var(--paper-edge))]"></div>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`h-8 w-8 p-0 ${isHeading1 ? 'bg-[hsl(22_65%_28%)] text-white' : 'hover:bg-[hsl(22_65%_28%)]/10'} dark:hover:bg-[hsl(22_65%_28%)]/10`}
          onClick={setHeading1}
          title="Heading 1"
        >
          <Heading1 className="h-4 w-4 dark:text-white" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`h-8 w-8 p-0 ${isHeading2 ? 'bg-[hsl(22_65%_28%)] text-white' : 'hover:bg-[hsl(22_65%_28%)]/10'} dark:hover:bg-[hsl(22_65%_28%)]/10`}
          onClick={setHeading2}
          title="Heading 2"
        >
          <Heading2 className="h-4 w-4 dark:text-white" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`h-8 w-8 p-0 ${isHeading3 ? 'bg-[hsl(22_65%_28%)] text-white' : 'hover:bg-[hsl(22_65%_28%)]/10'} dark:hover:bg-[hsl(22_65%_28%)]/10`}
          onClick={setHeading3}
          title="Heading 3"
        >
          <Heading3 className="h-4 w-4 dark:text-white" />
        </Button>
        <div className="w-px h-6 bg-[hsl(var(--paper-edge))] mx-1 dark:bg-[hsl(var(--paper-edge))]"></div>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`h-8 w-8 p-0 ${isCodeBlock ? 'bg-[hsl(22_65%_28%)] text-white' : 'hover:bg-[hsl(22_65%_28%)]/10'} dark:hover:bg-[hsl(22_65%_28%)]/10`}
          onClick={setCodeBlock}
          title="Code Block"
        >
          <Code className="h-4 w-4 dark:text-white" />
        </Button>
        <div className="w-px h-6 bg-[hsl(var(--paper-edge))] mx-1 dark:bg-[hsl(var(--paper-edge))]"></div>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`h-8 w-8 p-0 ${textAlign === 'left' ? 'bg-[hsl(22_65%_28%)] text-white' : 'hover:bg-[hsl(22_65%_28%)]/10'} dark:hover:bg-[hsl(22_65%_28%)]/10`}
          onClick={setTextAlignLeft}
          title="Align Left"
        >
          <AlignLeft className="h-4 w-4 dark:text-white" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`h-8 w-8 p-0 ${textAlign === 'center' ? 'bg-[hsl(22_65%_28%)] text-white' : 'hover:bg-[hsl(22_65%_28%)]/10'} dark:hover:bg-[hsl(22_65%_28%)]/10`}
          onClick={setTextAlignCenter}
          title="Align Center"
        >
          <AlignCenter className="h-4 w-4 dark:text-white" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`h-8 w-8 p-0 ${textAlign === 'right' ? 'bg-[hsl(22_65%_28%)] text-white' : 'hover:bg-[hsl(22_65%_28%)]/10'} dark:hover:bg-[hsl(22_65%_28%)]/10`}
          onClick={setTextAlignRight}
          title="Align Right"
        >
          <AlignRight className="h-4 w-4 dark:text-white" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`h-8 w-8 p-0 ${textAlign === 'justify' ? 'bg-[hsl(22_65%_28%)] text-white' : 'hover:bg-[hsl(22_65%_28%)]/10'} dark:hover:bg-[hsl(22_65%_28%)]/10`}
          onClick={setTextAlignJustify}
          title="Align Justify"
        >
          <AlignJustify className="h-4 w-4 dark:text-white" />
        </Button>
        <div className="w-px h-6 bg-[hsl(var(--paper-edge))] mx-1 dark:bg-[hsl(var(--paper-edge))]"></div>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`h-8 w-8 p-0 ${isBlockquote ? 'bg-[hsl(22_65%_28%)] text-white' : 'hover:bg-[hsl(22_65%_28%)]/10'} dark:hover:bg-[hsl(22_65%_28%)]/10`}
          onClick={toggleBlockquote}
          title="Blockquote"
        >
          <Quote className="h-4 w-4 dark:text-white" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          className={`h-8 w-8 p-0 hover:bg-[hsl(22_65%_28%)]/10 dark:hover:bg-[hsl(22_65%_28%)]/10`}
          onClick={setHorizontalRule}
          title="Horizontal Rule"
        >
          <Minus className="h-4 w-4 dark:text-white" />
        </Button>
      </div>
      
      {/* Editor Content */}
      <div className="p-4 min-h-[200px] max-h-[400px] overflow-y-auto dark:bg-[hsl(var(--paper))] dark:text-white">
        <EditorContent editor={editor} className="dark:text-white tiptap" />
      </div>
    </div>
  );
};